import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismMessage, PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import SettingsTransactionMappingCellsTdMappingDropdown from 'lumen/components/settings/transaction-mapping/cells/td-mapping-dropdown';
import SettingsTransactionMappingCellsTdOurCategory from 'lumen/components/settings/transaction-mapping/cells/td-our-category';
import SettingsTransactionMappingDropdownMapping from 'lumen/components/settings/transaction-mapping/dropdown-mapping';
import type {
  TransactionMappingHierarchyCategory,
  TransactionMappingItem,
} from 'lumen/models';
import type {
  Category,
  SubCategory,
} from 'lumen/utils/constants/transaction-mapping/constants';

interface SettingsTransactionMappingTableMappingSignature {
  Args: {
    handleMapItems: (
      items: TransactionMappingItem[],
      category: Category,
      subCategory: SubCategory,
    ) => void;
    hierarchyCategories: TransactionMappingHierarchyCategory[];
    isLoading: boolean;
    mappingItems: TransactionMappingItem[];
  };
}

const COLUMN_PROPERTIES = {
  TRANSACTION_CODE: 'transactionCode',
  DESCRIPTION: 'description',
  PMS_CATEGORY: 'pmsCategory',
  PMS_SUB_CATEGORY: 'pmsSubCategory',
  HOTEL_NAME: 'hotelName',
  OUR_CATEGORY: 'ourCategory',
  MAPPING: 'mapping',
} as const;

export default class SettingsTransactionMappingTableMapping extends Component<SettingsTransactionMappingTableMappingSignature> {
  @service declare intl: Services['intl'];

  @tracked selectedRowIds: string[] = [];

  initialSorts = [
    {
      valuePath: COLUMN_PROPERTIES.TRANSACTION_CODE,
      isAscending: true,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('settings.transaction_mapping.transaction_code'),
        valuePath: COLUMN_PROPERTIES.TRANSACTION_CODE,
        textAlign: 'left' as const,
        width: 200,
      },
      {
        name: this.intl.t('general.description'),
        valuePath: COLUMN_PROPERTIES.DESCRIPTION,
        textAlign: 'left' as const,
        width: 250,
      },
      {
        name: this.intl.t('settings.transaction_mapping.pms_category'),
        valuePath: COLUMN_PROPERTIES.PMS_CATEGORY,
        textAlign: 'left' as const,
        width: 150,
      },
      {
        name: this.intl.t('settings.transaction_mapping.pms_sub_category'),
        valuePath: COLUMN_PROPERTIES.PMS_SUB_CATEGORY,
        textAlign: 'left' as const,
        width: 150,
      },
      {
        name: this.intl.t('general.hotelname'),
        valuePath: COLUMN_PROPERTIES.HOTEL_NAME,
        textAlign: 'left' as const,
        width: 250,
      },
      {
        name: this.intl.t('settings.transaction_mapping.lighthouse_category'),
        valuePath: COLUMN_PROPERTIES.OUR_CATEGORY,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.OUR_CATEGORY}`,
        textAlign: 'left' as const,
        tdComponent: SettingsTransactionMappingCellsTdOurCategory,
        tdClassNames: 'table-column--with-unmapped-background',
        width: 300,
      },
      {
        name: '',
        valuePath: COLUMN_PROPERTIES.MAPPING,
        textAlign: 'right' as const,
        isSortable: false,
        tdComponent:
          // only show dropdown if no rows are selected
          this.selectedMappingItems.length === 0
            ? SettingsTransactionMappingCellsTdMappingDropdown
            : '',
        tdClassNames: 'table-column--with-unmapped-background',
        width: 200,
        hierarchyCategories: this.args.hierarchyCategories,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get itemsByTransactionCode() {
    const map = this.args.mappingItems.reduce((acc, item) => {
      const group = acc.get(item.transactionCode);

      if (group) {
        group.push(item);
      } else {
        acc.set(item.transactionCode, [item]);
      }

      return acc;
    }, new Map<string, TransactionMappingItem[]>());

    return Object.fromEntries(map);
  }

  // Preserve reference for equality check in ember-table
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return Object.entries(this.itemsByTransactionCode).map(
      ([transactionCode, items]) =>
        this.getRowDataForGroup(transactionCode, items),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMappingItems() {
    return (
      this.rows
        .flatMap((item) => {
          return [item, ...(item.children ?? [])];
        })
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        .filter((item) => this.selectedRowIds.includes(item.id))
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCategoryDisplayValueForGroup(items: TransactionMappingItem[]) {
    const [firstItem] = items;

    if (!firstItem) {
      return undefined;
    }

    const isMixed = items.some(
      (item) =>
        item.ourCategory !== firstItem.ourCategory ||
        item.ourSubCategory !== firstItem.ourSubCategory,
    );

    if (isMixed) {
      return undefined;
    }

    return {
      category: firstItem.ourCategory,
      subCategory: firstItem.ourSubCategory,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowDataForGroup(transactionCode: string, items: TransactionMappingItem[]) {
    const numberOfHotelsMessage = this.intl.t('general.hotel.count', {
      count: items.length,
    });

    const mappedCategoryDisplayValue =
      this.getCategoryDisplayValueForGroup(items);
    const mappedCategory =
      mappedCategoryDisplayValue?.subCategory ??
      this.intl.t('settings.transaction_mapping.mixed');

    const isUnmapped =
      mappedCategoryDisplayValue?.category === 'unmapped' &&
      mappedCategoryDisplayValue?.subCategory === 'unmapped';

    const children = items.map((item) => this.getRowDataForItem(item));

    return {
      id: transactionCode,
      className: isUnmapped
        ? 'table-row--group table-row--unmapped'
        : 'table-row--group',
      children,

      [COLUMN_PROPERTIES.TRANSACTION_CODE]: transactionCode,
      [COLUMN_PROPERTIES.HOTEL_NAME]: numberOfHotelsMessage,
      [COLUMN_PROPERTIES.OUR_CATEGORY]: mappedCategory,

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      mapItem: (category: Category, subCategory: SubCategory) => {
        const childItems = children.map((child) => child.item);
        this.args.handleMapItems(childItems, category, subCategory);
        this.selectedRowIds = [];
      },

      displayValue: {
        [COLUMN_PROPERTIES.OUR_CATEGORY]: mappedCategoryDisplayValue,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowDataForItem(item: TransactionMappingItem) {
    return {
      item,
      id: item.id,
      className: item.isUnmapped ? 'table-row--unmapped' : '',

      [COLUMN_PROPERTIES.DESCRIPTION]: item.pmsDescription,
      [COLUMN_PROPERTIES.PMS_CATEGORY]: item.pmsCategory,
      [COLUMN_PROPERTIES.PMS_SUB_CATEGORY]: item.pmsSubCategory,
      [COLUMN_PROPERTIES.HOTEL_NAME]: item.subscription.name,
      [COLUMN_PROPERTIES.OUR_CATEGORY]: item.ourSubCategory,

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      mapItem: (category: Category, subCategory: SubCategory) => {
        this.args.handleMapItems([item], category, subCategory);
        this.selectedRowIds = [];
      },

      displayValue: {
        [COLUMN_PROPERTIES.OUR_CATEGORY]: {
          category: item.ourCategory,
          subCategory: item.ourSubCategory,
        },
      },
    };
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onBulkEditCategorySelect(category: Category, subCategory: SubCategory) {
    const items = this.selectedMappingItems.flatMap((item) => {
      if ('children' in item) {
        return item.children.map((child) => child.item);
      }

      return item.item;
    });

    this.args.handleMapItems(items, category, subCategory);
    this.selectedRowIds = [];
  }

  @action setSelectedMappingItems(
    items: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    assert('items should be an array', Array.isArray(items));
    this.selectedRowIds = items.map((item) => item['id'] as string);
  }

  <template>
    <PrismTable
      @checkboxSelectionMode="multiple"
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @fillMode="last-column"
      @initialSorts={{this.initialSorts}}
      @isLoading={{@isLoading}}
      @nrOfEmptyRows={{10}}
      @nrOfLoadingRows={{10}}
      @onRowSelect={{this.setSelectedMappingItems}}
      @removeTableBorder={{true}}
      @renderAll={{false}}
      @rowSelection={{this.selectedMappingItems}}
      @rows={{this.rows}}
      @widthConstraint="gte-container"
      class="table-transaction-mapping"
      data-test-id="table-transaction-mapping"
    >
      <:bulkEditRow as |bulkEditRow|>
        <div class="flex items-center gap-200">
          <span class="text-neutral-emphasis">
            <span
              class="text-200--bold text-neutral-emphasis"
            >{{bulkEditRow.numberOfSelectedRows}}</span>
            {{t "general.selected"}}
          </span>

          <SettingsTransactionMappingDropdownMapping
            @hierarchyCategories={{@hierarchyCategories}}
            @onCategorySelect={{this.onBulkEditCategorySelect}}
          />
        </div>
      </:bulkEditRow>
      <:emptyState>
        <div
          class="flex h-full items-center justify-center"
          data-test-id="empty-state"
        >
          <PrismMessage
            @description=""
            @illustration="100x100_search"
            @title={{t
              "settings.central_mapping.mapping.nothing_matches_filters"
            }}
          />
        </div>
      </:emptyState>
    </PrismTable>
  </template>
}
